import type {
  Exchanges,
  Stock,
  Cryptocurrency,
  ForexPair,
  ETF,
  Commodity,
  Index,
  TechnicalIndicators,
  ScreenerPreset,
  Colors,
} from '../types';

// Exchange configurations
export const EXCHANGES: Exchanges = {
  JSE: {
    code: 'JSE',
    name: 'Johannesburg Stock Exchange',
    suffix: '.JSE',
    currency: 'ZAR',
    timezone: 'Africa/Johannesburg',
  },
  US: {
    code: 'US',
    name: 'US Markets',
    suffix: '.US',
    currency: 'USD',
    timezone: 'America/New_York',
  },
  CRYPTO: {
    code: 'CC',
    name: 'Cryptocurrency',
    suffix: '.CC',
    currency: 'USD',
    timezone: 'UTC',
  },
  FOREX: {
    code: 'FOREX',
    name: 'Foreign Exchange',
    suffix: '.FOREX',
    currency: 'Various',
    timezone: 'UTC',
  },
};

// JSE Top 40 + Popular Mid-Caps - comprehensive list for trading platform
export const JSE_STOCKS: Stock[] = [
  // Technology & Internet
  { symbol: 'NPN.JSE', name: 'Naspers Limited', sector: 'Technology' },
  { symbol: 'PRX.JSE', name: 'Prosus NV', sector: 'Technology' },

  // Mining & Resources - Top 40 + Major Producers
  { symbol: 'AGL.JSE', name: 'Anglo American', sector: 'Mining' },
  { symbol: 'BHP.JSE', name: 'BHP Group', sector: 'Mining' },
  { symbol: 'ANG.JSE', name: 'AngloGold Ashanti', sector: 'Mining' },
  { symbol: 'SSW.JSE', name: 'Sibanye Stillwater', sector: 'Mining' },
  { symbol: 'IMP.JSE', name: 'Impala Platinum', sector: 'Mining' },
  { symbol: 'AMS.JSE', name: 'Anglo American Platinum', sector: 'Mining' },
  { symbol: 'GFI.JSE', name: 'Gold Fields', sector: 'Mining' },
  { symbol: 'GLN.JSE', name: 'Glencore', sector: 'Mining' },
  { symbol: 'NPH.JSE', name: 'Northam Platinum', sector: 'Mining' },
  { symbol: 'HAR.JSE', name: 'Harmony Gold', sector: 'Mining' },
  { symbol: 'ARI.JSE', name: 'African Rainbow Minerals', sector: 'Mining' },
  { symbol: 'TGA.JSE', name: 'Thungela Resources', sector: 'Mining' },
  { symbol: 'KIO.JSE', name: 'Kumba Iron Ore', sector: 'Mining' },
  { symbol: 'EXX.JSE', name: 'Exxaro Resources', sector: 'Mining' },
  { symbol: 'RBP.JSE', name: 'Royal Bafokeng Platinum', sector: 'Mining' },
  { symbol: 'PAN.JSE', name: 'Pan African Resources', sector: 'Mining' },
  { symbol: 'DRD.JSE', name: 'DRDGOLD', sector: 'Mining' },

  // Energy & Chemicals
  { symbol: 'SOL.JSE', name: 'Sasol Limited', sector: 'Energy' },

  // Financials - Banks
  { symbol: 'SBK.JSE', name: 'Standard Bank', sector: 'Financials' },
  { symbol: 'FSR.JSE', name: 'FirstRand', sector: 'Financials' },
  { symbol: 'ABG.JSE', name: 'Absa Group', sector: 'Financials' },
  { symbol: 'NED.JSE', name: 'Nedbank', sector: 'Financials' },
  { symbol: 'CPI.JSE', name: 'Capitec Bank', sector: 'Financials' },
  { symbol: 'INL.JSE', name: 'Investec Limited', sector: 'Financials' },

  // Financials - Insurance & Investment
  { symbol: 'DSY.JSE', name: 'Discovery Limited', sector: 'Financials' },
  { symbol: 'SLM.JSE', name: 'Sanlam', sector: 'Financials' },
  { symbol: 'OMU.JSE', name: 'Old Mutual', sector: 'Financials' },

  // Telecommunications
  { symbol: 'MTN.JSE', name: 'MTN Group', sector: 'Telecoms' },
  { symbol: 'VOD.JSE', name: 'Vodacom Group', sector: 'Telecoms' },

  // Retail & Consumer
  { symbol: 'SHP.JSE', name: 'Shoprite Holdings', sector: 'Retail' },
  { symbol: 'WHL.JSE', name: 'Woolworths Holdings', sector: 'Retail' },
  { symbol: 'PIK.JSE', name: 'Pick n Pay Stores', sector: 'Retail' },
  { symbol: 'MRP.JSE', name: 'Mr Price Group', sector: 'Retail' },
  { symbol: 'TFG.JSE', name: 'The Foschini Group', sector: 'Retail' },
  { symbol: 'TRU.JSE', name: 'Truworths International', sector: 'Retail' },
  { symbol: 'CLS.JSE', name: 'Clicks Group', sector: 'Retail' },
  { symbol: 'SPP.JSE', name: 'Spar Group', sector: 'Retail' },

  // Consumer Goods & Manufacturing
  { symbol: 'CFR.JSE', name: 'Richemont', sector: 'Consumer Goods' },
  { symbol: 'BTI.JSE', name: 'British American Tobacco', sector: 'Consumer Goods' },
  { symbol: 'TBS.JSE', name: 'Tiger Brands', sector: 'Consumer Goods' },
  { symbol: 'AVI.JSE', name: 'AVI Limited', sector: 'Consumer Goods' },

  // Media & Entertainment
  { symbol: 'MCG.JSE', name: 'MultiChoice Group', sector: 'Media' },

  // Industrials & Conglomerates
  { symbol: 'REM.JSE', name: 'Remgro', sector: 'Conglomerate' },
  { symbol: 'BVT.JSE', name: 'Bidvest Group', sector: 'Industrials' },
  { symbol: 'BID.JSE', name: 'Bid Corporation', sector: 'Industrials' },
  { symbol: 'BAW.JSE', name: 'Barloworld', sector: 'Industrials' },
  { symbol: 'MNP.JSE', name: 'Mondi PLC', sector: 'Industrials' },
  { symbol: 'SAP.JSE', name: 'Sappi Limited', sector: 'Industrials' },

  // Property & REITs
  { symbol: 'GRT.JSE', name: 'Growthpoint Properties', sector: 'Property' },
  { symbol: 'RDF.JSE', name: 'Redefine Properties', sector: 'Property' },
  { symbol: 'VKE.JSE', name: 'Vukile Property Fund', sector: 'Property' },

  // Healthcare & Pharmaceuticals
  { symbol: 'NTC.JSE', name: 'Netcare', sector: 'Healthcare' },
  { symbol: 'LHC.JSE', name: 'Life Healthcare', sector: 'Healthcare' },
  { symbol: 'MEI.JSE', name: 'Mediclinic International', sector: 'Healthcare' },
  { symbol: 'APN.JSE', name: 'Aspen Pharmacare', sector: 'Healthcare' },
];

// Popular US stocks - Major companies across sectors
export const US_STOCKS: Stock[] = [
  // Technology
  { symbol: 'AAPL.US', name: 'Apple Inc', sector: 'Technology' },
  { symbol: 'MSFT.US', name: 'Microsoft Corporation', sector: 'Technology' },
  { symbol: 'NVDA.US', name: 'NVIDIA Corporation', sector: 'Technology' },
  { symbol: 'GOOGL.US', name: 'Alphabet Inc (Class A)', sector: 'Technology' },
  { symbol: 'META.US', name: 'Meta Platforms', sector: 'Technology' },
  { symbol: 'AVGO.US', name: 'Broadcom Inc', sector: 'Technology' },
  { symbol: 'ORCL.US', name: 'Oracle Corporation', sector: 'Technology' },
  { symbol: 'AMD.US', name: 'Advanced Micro Devices', sector: 'Technology' },
  { symbol: 'CSCO.US', name: 'Cisco Systems', sector: 'Technology' },
  { symbol: 'IBM.US', name: 'IBM Corporation', sector: 'Technology' },
  { symbol: 'INTU.US', name: 'Intuit Inc', sector: 'Technology' },
  { symbol: 'NOW.US', name: 'ServiceNow Inc', sector: 'Technology' },
  { symbol: 'CRM.US', name: 'Salesforce Inc', sector: 'Technology' },
  { symbol: 'NFLX.US', name: 'Netflix Inc', sector: 'Technology' },

  // Consumer Discretionary
  { symbol: 'AMZN.US', name: 'Amazon.com Inc', sector: 'Consumer Discretionary' },
  { symbol: 'TSLA.US', name: 'Tesla Inc', sector: 'Consumer Discretionary' },
  { symbol: 'HD.US', name: 'Home Depot Inc', sector: 'Consumer Discretionary' },
  { symbol: 'MCD.US', name: "McDonald's Corporation", sector: 'Consumer Discretionary' },
  { symbol: 'BKNG.US', name: 'Booking Holdings', sector: 'Consumer Discretionary' },
  { symbol: 'UBER.US', name: 'Uber Technologies', sector: 'Consumer Discretionary' },

  // Consumer Staples
  { symbol: 'WMT.US', name: 'Walmart Inc', sector: 'Consumer Staples' },
  { symbol: 'COST.US', name: 'Costco Wholesale', sector: 'Consumer Staples' },
  { symbol: 'PG.US', name: 'Procter & Gamble Co', sector: 'Consumer Staples' },
  { symbol: 'KO.US', name: 'Coca-Cola Company', sector: 'Consumer Staples' },
  { symbol: 'PEP.US', name: 'PepsiCo Inc', sector: 'Consumer Staples' },

  // Healthcare
  { symbol: 'LLY.US', name: 'Eli Lilly and Company', sector: 'Healthcare' },
  { symbol: 'UNH.US', name: 'UnitedHealth Group', sector: 'Healthcare' },
  { symbol: 'JNJ.US', name: 'Johnson & Johnson', sector: 'Healthcare' },
  { symbol: 'ABBV.US', name: 'AbbVie Inc', sector: 'Healthcare' },
  { symbol: 'MRK.US', name: 'Merck & Co', sector: 'Healthcare' },
  { symbol: 'TMO.US', name: 'Thermo Fisher Scientific', sector: 'Healthcare' },
  { symbol: 'ISRG.US', name: 'Intuitive Surgical', sector: 'Healthcare' },
  { symbol: 'PFE.US', name: 'Pfizer Inc', sector: 'Healthcare' },

  // Financials
  { symbol: 'BRK-B.US', name: 'Berkshire Hathaway B', sector: 'Financials' },
  { symbol: 'JPM.US', name: 'JPMorgan Chase & Co', sector: 'Financials' },
  { symbol: 'V.US', name: 'Visa Inc', sector: 'Financials' },
  { symbol: 'MA.US', name: 'Mastercard Inc', sector: 'Financials' },
  { symbol: 'BAC.US', name: 'Bank of America Corp', sector: 'Financials' },
  { symbol: 'GS.US', name: 'Goldman Sachs Group', sector: 'Financials' },
  { symbol: 'AXP.US', name: 'American Express Co', sector: 'Financials' },
  { symbol: 'SPGI.US', name: 'S&P Global Inc', sector: 'Financials' },

  // Energy
  { symbol: 'XOM.US', name: 'Exxon Mobil Corporation', sector: 'Energy' },
  { symbol: 'CVX.US', name: 'Chevron Corporation', sector: 'Energy' },

  // Industrials
  { symbol: 'GE.US', name: 'GE Aerospace', sector: 'Industrials' },
  { symbol: 'CAT.US', name: 'Caterpillar Inc', sector: 'Industrials' },
  { symbol: 'ACN.US', name: 'Accenture plc', sector: 'Industrials' },
  { symbol: 'BA.US', name: 'Boeing Company', sector: 'Industrials' },

  // Materials
  { symbol: 'LIN.US', name: 'Linde plc', sector: 'Materials' },

  // Communications
  { symbol: 'T.US', name: 'AT&T Inc', sector: 'Communications' },
];

// London Stock Exchange — FTSE 100 Top 50 by market cap
export const LSE_STOCKS: Stock[] = [
  // Healthcare / Pharma
  { symbol: 'AZN.LSE', name: 'AstraZeneca', sector: 'Healthcare' },
  { symbol: 'GSK.LSE', name: 'GSK plc', sector: 'Healthcare' },
  { symbol: 'HLN.LSE', name: 'Haleon', sector: 'Healthcare' },
  { symbol: 'SN.LSE', name: 'Smith & Nephew', sector: 'Healthcare' },
  { symbol: 'HIK.LSE', name: 'Hikma Pharmaceuticals', sector: 'Healthcare' },

  // Energy
  { symbol: 'SHEL.LSE', name: 'Shell plc', sector: 'Energy' },
  { symbol: 'BP.LSE', name: 'BP plc', sector: 'Energy' },
  { symbol: 'SSE.LSE', name: 'SSE plc', sector: 'Energy/Utilities' },

  // Mining & Materials
  { symbol: 'RIO.LSE', name: 'Rio Tinto', sector: 'Mining' },
  { symbol: 'GLEN.LSE', name: 'Glencore', sector: 'Mining' },
  { symbol: 'AAL.LSE', name: 'Anglo American', sector: 'Mining' },
  { symbol: 'ANTO.LSE', name: 'Antofagasta', sector: 'Mining' },
  { symbol: 'FRES.LSE', name: 'Fresnillo', sector: 'Mining' },
  { symbol: 'MNDI.LSE', name: 'Mondi', sector: 'Materials' },

  // Financials
  { symbol: 'HSBA.LSE', name: 'HSBC Holdings', sector: 'Financials' },
  { symbol: 'BARC.LSE', name: 'Barclays', sector: 'Financials' },
  { symbol: 'LLOY.LSE', name: 'Lloyds Banking Group', sector: 'Financials' },
  { symbol: 'NWG.LSE', name: 'NatWest Group', sector: 'Financials' },
  { symbol: 'STAN.LSE', name: 'Standard Chartered', sector: 'Financials' },
  { symbol: 'LSEG.LSE', name: 'London Stock Exchange Group', sector: 'Financials' },
  { symbol: 'III.LSE', name: '3i Group', sector: 'Financials' },
  { symbol: 'LGEN.LSE', name: 'Legal & General', sector: 'Financials' },
  { symbol: 'AV.LSE', name: 'Aviva', sector: 'Financials' },
  { symbol: 'PRU.LSE', name: 'Prudential', sector: 'Financials' },
  { symbol: 'MNG.LSE', name: 'M&G plc', sector: 'Financials' },

  // Consumer Staples
  { symbol: 'ULVR.LSE', name: 'Unilever', sector: 'Consumer Staples' },
  { symbol: 'DGE.LSE', name: 'Diageo', sector: 'Consumer Staples' },
  { symbol: 'RKT.LSE', name: 'Reckitt Benckiser', sector: 'Consumer Staples' },
  { symbol: 'BATS.LSE', name: 'British American Tobacco', sector: 'Consumer Staples' },
  { symbol: 'IMB.LSE', name: 'Imperial Brands', sector: 'Consumer Staples' },

  // Consumer Discretionary
  { symbol: 'FLTR.LSE', name: 'Flutter Entertainment', sector: 'Consumer Discretionary' },
  { symbol: 'WTB.LSE', name: 'Whitbread', sector: 'Consumer Discretionary' },
  { symbol: 'JD.LSE', name: 'JD Sports Fashion', sector: 'Consumer Discretionary' },

  // Industrials / Defence
  { symbol: 'BA.LSE', name: 'BAE Systems', sector: 'Industrials' },
  { symbol: 'RR.LSE', name: 'Rolls-Royce Holdings', sector: 'Industrials' },
  { symbol: 'CPG.LSE', name: 'Compass Group', sector: 'Industrials' },

  // Technology / Analytics
  { symbol: 'REL.LSE', name: 'RELX plc', sector: 'Technology' },
  { symbol: 'SGE.LSE', name: 'Sage Group', sector: 'Technology' },
  { symbol: 'EXPN.LSE', name: 'Experian', sector: 'Technology' },
  { symbol: 'AUTO.LSE', name: 'Auto Trader Group', sector: 'Technology' },
  { symbol: 'INF.LSE', name: 'Informa', sector: 'Technology' },
  { symbol: 'PSON.LSE', name: 'Pearson', sector: 'Technology' },

  // Telecoms
  { symbol: 'VOD.LSE', name: 'Vodafone Group', sector: 'Telecoms' },
  { symbol: 'BT-A.LSE', name: 'BT Group', sector: 'Telecoms' },

  // Utilities
  { symbol: 'NG.LSE', name: 'National Grid', sector: 'Utilities' },
  { symbol: 'SVT.LSE', name: 'Severn Trent', sector: 'Utilities' },

  // Real Estate
  { symbol: 'SGRO.LSE', name: 'Segro', sector: 'Real Estate' },
  { symbol: 'LAND.LSE', name: 'Land Securities', sector: 'Real Estate' },
  { symbol: 'BKG.LSE', name: 'Berkeley Group', sector: 'Real Estate' },
  { symbol: 'PSN.LSE', name: 'Persimmon', sector: 'Real Estate' },
  { symbol: 'TW.LSE', name: 'Taylor Wimpey', sector: 'Real Estate' },

  // Communications / Media
  { symbol: 'WPP.LSE', name: 'WPP plc', sector: 'Communications' },
];

// Asian markets — Hong Kong, ASX
export const ASIA_STOCKS: Stock[] = [
  // ── Hong Kong (HSI) ─────────────────────────────────────────────
  { symbol: '0700.HK', name: 'Tencent', sector: 'Technology' },
  { symbol: '9988.HK', name: 'Alibaba', sector: 'E-commerce' },
  { symbol: '0005.HK', name: 'HSBC Holdings', sector: 'Banking' },
  { symbol: '1299.HK', name: 'AIA Group', sector: 'Insurance' },
  { symbol: '3690.HK', name: 'Meituan', sector: 'Technology' },
  { symbol: '0939.HK', name: 'China Construction Bank', sector: 'Banking' },
  { symbol: '1398.HK', name: 'ICBC', sector: 'Banking' },
  { symbol: '2318.HK', name: 'Ping An Insurance', sector: 'Insurance' },
  { symbol: '0941.HK', name: 'China Mobile', sector: 'Telecoms' },
  { symbol: '1810.HK', name: 'Xiaomi', sector: 'Electronics' },
  { symbol: '0388.HK', name: 'HK Exchanges', sector: 'Financial' },
  { symbol: '0883.HK', name: 'CNOOC', sector: 'Energy' },
  { symbol: '9618.HK', name: 'JD.com', sector: 'E-commerce' },
  { symbol: '0175.HK', name: 'Geely Auto', sector: 'Automotive' },
  { symbol: '2020.HK', name: 'ANTA Sports', sector: 'Consumer' },
  { symbol: '0857.HK', name: 'PetroChina', sector: 'Energy' },
  { symbol: '2382.HK', name: 'Sunny Optical', sector: 'Technology' },
  { symbol: '1093.HK', name: 'CSPC Pharma', sector: 'Healthcare' },
  { symbol: '2269.HK', name: 'WuXi Biologics', sector: 'Healthcare' },
  { symbol: '0066.HK', name: 'MTR Corp', sector: 'Transport' },
  { symbol: '0011.HK', name: 'Hang Seng Bank', sector: 'Banking' },
  { symbol: '1038.HK', name: 'CK Infrastructure', sector: 'Utilities' },
  { symbol: '0001.HK', name: 'CK Hutchison', sector: 'Conglomerate' },
  { symbol: '0016.HK', name: 'Sun Hung Kai Prop', sector: 'Real Estate' },
  { symbol: '0027.HK', name: 'Galaxy Entertainment', sector: 'Gaming' },
  // ── ASX (Australia) ─────────────────────────────────────────────
  { symbol: 'CBA.AU', name: 'Commonwealth Bank', sector: 'Banking' },
  { symbol: 'BHP.AU', name: 'BHP Group', sector: 'Mining' },
  { symbol: 'CSL.AU', name: 'CSL Limited', sector: 'Healthcare' },
  { symbol: 'NAB.AU', name: 'National Australia Bank', sector: 'Banking' },
  { symbol: 'WBC.AU', name: 'Westpac', sector: 'Banking' },
  { symbol: 'ANZ.AU', name: 'ANZ Group', sector: 'Banking' },
  { symbol: 'WES.AU', name: 'Wesfarmers', sector: 'Consumer' },
  { symbol: 'MQG.AU', name: 'Macquarie Group', sector: 'Financial' },
  { symbol: 'RIO.AU', name: 'Rio Tinto', sector: 'Mining' },
  { symbol: 'WOW.AU', name: 'Woolworths AU', sector: 'Retail' },
  { symbol: 'FMG.AU', name: 'Fortescue', sector: 'Mining' },
  { symbol: 'TLS.AU', name: 'Telstra', sector: 'Telecoms' },
  { symbol: 'ALL.AU', name: 'Aristocrat Leisure', sector: 'Gaming' },
  { symbol: 'COL.AU', name: 'Coles Group', sector: 'Retail' },
  { symbol: 'GMG.AU', name: 'Goodman Group', sector: 'Real Estate' },
  { symbol: 'WDS.AU', name: 'Woodside Energy', sector: 'Energy' },
  { symbol: 'STO.AU', name: 'Santos', sector: 'Energy' },
  { symbol: 'REA.AU', name: 'REA Group', sector: 'Technology' },
  { symbol: 'TCL.AU', name: 'Transurban', sector: 'Infrastructure' },
  { symbol: 'MIN.AU', name: 'Mineral Resources', sector: 'Mining' },
  { symbol: 'ASX.AU', name: 'ASX Limited', sector: 'Financial' },
  { symbol: 'CPU.AU', name: 'Computershare', sector: 'Technology' },
  { symbol: 'AGL.AU', name: 'AGL Energy', sector: 'Utilities' },
  { symbol: 'ORG.AU', name: 'Origin Energy', sector: 'Energy' },
  { symbol: 'QBE.AU', name: 'QBE Insurance', sector: 'Insurance' },
];

// Major cryptocurrencies
export const CRYPTOCURRENCIES: Cryptocurrency[] = [
  { symbol: 'BTC-USD.CC', name: 'Bitcoin', code: 'BTC' },
  { symbol: 'ETH-USD.CC', name: 'Ethereum', code: 'ETH' },
  { symbol: 'SOL-USD.CC', name: 'Solana', code: 'SOL' },
  { symbol: 'XRP-USD.CC', name: 'Ripple', code: 'XRP' },
  { symbol: 'ADA-USD.CC', name: 'Cardano', code: 'ADA' },
  { symbol: 'DOGE-USD.CC', name: 'Dogecoin', code: 'DOGE' },
];

// Forex pairs (Major pairs + ZAR-focused)
export const FOREX_PAIRS: ForexPair[] = [
  // Major Forex Pairs
  { symbol: 'EURUSD.FOREX', name: 'Euro / US Dollar', base: 'EUR', quote: 'USD' },
  { symbol: 'GBPUSD.FOREX', name: 'British Pound / US Dollar', base: 'GBP', quote: 'USD' },
  { symbol: 'USDJPY.FOREX', name: 'US Dollar / Japanese Yen', base: 'USD', quote: 'JPY' },
  { symbol: 'AUDUSD.FOREX', name: 'Australian Dollar / US Dollar', base: 'AUD', quote: 'USD' },
  { symbol: 'USDCHF.FOREX', name: 'US Dollar / Swiss Franc', base: 'USD', quote: 'CHF' },
  { symbol: 'USDCAD.FOREX', name: 'US Dollar / Canadian Dollar', base: 'USD', quote: 'CAD' },
  { symbol: 'NZDUSD.FOREX', name: 'New Zealand Dollar / US Dollar', base: 'NZD', quote: 'USD' },
  { symbol: 'EURGBP.FOREX', name: 'Euro / British Pound', base: 'EUR', quote: 'GBP' },
  { symbol: 'EURJPY.FOREX', name: 'Euro / Japanese Yen', base: 'EUR', quote: 'JPY' },
  { symbol: 'GBPJPY.FOREX', name: 'British Pound / Japanese Yen', base: 'GBP', quote: 'JPY' },
  // ZAR Pairs
  { symbol: 'USDZAR.FOREX', name: 'US Dollar / South African Rand', base: 'USD', quote: 'ZAR' },
  { symbol: 'EURZAR.FOREX', name: 'Euro / South African Rand', base: 'EUR', quote: 'ZAR' },
  { symbol: 'GBPZAR.FOREX', name: 'British Pound / South African Rand', base: 'GBP', quote: 'ZAR' },
  { symbol: 'AUDZAR.FOREX', name: 'Australian Dollar / South African Rand', base: 'AUD', quote: 'ZAR' },
  { symbol: 'JPYZAR.FOREX', name: 'Japanese Yen / South African Rand', base: 'JPY', quote: 'ZAR' },
];

// Popular ETFs
export const ETFS: ETF[] = [
  // US ETFs — broad market
  { symbol: 'SPY.US', name: 'SPDR S&P 500 ETF', category: 'Equity' },
  { symbol: 'IVV.US', name: 'iShares Core S&P 500 ETF', category: 'Equity' },
  { symbol: 'QQQ.US', name: 'Invesco QQQ Trust (Nasdaq 100)', category: 'Technology' },
  { symbol: 'VTI.US', name: 'Vanguard Total Stock Market ETF', category: 'Equity' },
  // Vanguard ETFs
  { symbol: 'VOO.US', name: 'Vanguard S&P 500 ETF', category: 'Equity' },
  { symbol: 'VGT.US', name: 'Vanguard Information Technology ETF', category: 'Technology' },
  { symbol: 'VUG.US', name: 'Vanguard Growth ETF', category: 'Growth' },
  { symbol: 'VXUS.US', name: 'Vanguard Total International Stock ETF', category: 'International' },
  { symbol: 'BND.US', name: 'Vanguard Total Bond Market ETF', category: 'Fixed Income' },
  // Sector & thematic
  { symbol: 'XLK.US', name: 'Technology Select Sector SPDR', category: 'Technology' },
  { symbol: 'GLD.US', name: 'SPDR Gold Shares', category: 'Commodity' },
  { symbol: 'ARKK.US', name: 'ARK Innovation ETF', category: 'Innovation' },
  // South African ETFs
  { symbol: 'STX40.JSE', name: 'Satrix 40 ETF', category: 'Equity' },
  { symbol: 'STXIND.JSE', name: 'Satrix Industrial ETF', category: 'Industrial' },
  { symbol: 'STXFIN.JSE', name: 'Satrix Financial ETF', category: 'Financials' },
  { symbol: 'STXRES.JSE', name: 'Satrix Resources ETF', category: 'Resources' },
  { symbol: 'STXPRO.JSE', name: 'Satrix Property ETF', category: 'Property' },
  { symbol: 'STXDIV.JSE', name: 'Satrix Dividend Plus ETF', category: 'Dividend' },
  { symbol: 'STXWDM.JSE', name: 'Satrix MSCI World Equity ETF', category: 'Global Equity' },
  { symbol: 'STXEMG.JSE', name: 'Satrix MSCI Emerging Markets ETF', category: 'Emerging Markets' },
  { symbol: 'STXRAF.JSE', name: 'Satrix RAFI 40 ETF', category: 'Equity' },
  { symbol: 'CSSP500.JSE', name: 'CoreShares S&P 500 ETF', category: 'US Equity' },
  { symbol: 'ASHGEQ.JSE', name: 'Ashburton Global Equity Feeder ETF', category: 'Global Equity' },
  { symbol: 'ETFTOP.JSE', name: '1nvest FTSE/JSE Top 40 ETF', category: 'Equity' },
];

// Commodities - Using ETFs for reliable real-time data
export const COMMODITIES: Commodity[] = [
  // Precious Metals ETFs
  { symbol: 'GLD.US', name: 'Gold (SPDR Gold Shares)', unit: 'per share' },
  { symbol: 'SLV.US', name: 'Silver (iShares Silver Trust)', unit: 'per share' },
  { symbol: 'PPLT.US', name: 'Platinum (Aberdeen Platinum)', unit: 'per share' },
  // Energy ETFs
  { symbol: 'USO.US', name: 'Crude Oil (US Oil Fund)', unit: 'per share' },
  { symbol: 'UNG.US', name: 'Natural Gas (US Gas Fund)', unit: 'per share' },
  // Forex Spot Prices (for reference/comparison)
  { symbol: 'XAUUSD.FOREX', name: 'Gold Spot Price', unit: 'per oz' },
  { symbol: 'XAGUSD.FOREX', name: 'Silver Spot Price', unit: 'per oz' },
  { symbol: 'XPTUSD.FOREX', name: 'Platinum Spot Price', unit: 'per oz' },
];

// Market indices
export const INDICES: Index[] = [
  { symbol: 'ALSI.INDX', name: 'JSE All Share Index (ALSI)', exchange: 'JSE' },
  { symbol: 'GSPC.INDX', name: 'S&P 500', exchange: 'US' },
  { symbol: 'IXIC.INDX', name: 'NASDAQ Composite', exchange: 'US' },
  { symbol: 'DJI.INDX', name: 'Dow Jones Industrial Average', exchange: 'US' },
];

// Technical indicator presets
export const TECHNICAL_INDICATORS: TechnicalIndicators = {
  SMA_50: { function: 'sma', period: 50, name: 'SMA (50)' },
  SMA_200: { function: 'sma', period: 200, name: 'SMA (200)' },
  RSI: { function: 'rsi', period: 14, name: 'RSI (14)' },
  MACD: { function: 'macd', fast_period: 12, slow_period: 26, signal_period: 9, name: 'MACD' },
  BBANDS: { function: 'bbands', period: 20, name: 'Bollinger Bands' },
};

// Screener filter presets
export const SCREENER_PRESETS: Record<string, ScreenerPreset> = {
  VALUE_STOCKS: {
    name: 'Value Stocks',
    filters: {
      pe_ratio_max: 15,
      pb_ratio_max: 1.5,
      dividend_yield_min: 2,
    },
  },
  GROWTH_STOCKS: {
    name: 'Growth Stocks',
    filters: {
      revenue_growth_min: 10,
      earnings_growth_min: 10,
    },
  },
  OVERSOLD: {
    name: 'Oversold (RSI < 30)',
    filters: {
      rsi_max: 30,
    },
  },
  MOMENTUM: {
    name: 'Momentum Play',
    filters: {
      change_percent_min: 5,
      volume_vs_avg_min: 1.5,
    },
  },
};

// Color scheme matching design specs
export const COLORS: Colors = {
  primary: '#1E3A5F',
  secondary: '#2DD4BF',
  accent: '#F59E0B',
  background: '#0F172A',
  surface: '#1E293B',
  card: '#334155',
  gain: '#10B981',
  loss: '#EF4444',
  neutral: '#6B7280',
  warning: '#F59E0B',
};
